// ─────────────────────────────────────────────────────────────────────────
// Finance atoms — shared presentational building blocks for the Billing /
// Payroll module. Purely presentational: no fetching, no routing.
//
// Back-compat: FinanceStatusPill / FinanceKpiRow / FinanceHoursGrid keep
// their original prop shapes ({status}, {items:[{label,value,hint}]}, {days}).
// ─────────────────────────────────────────────────────────────────────────
import { Tooltip } from 'antd';
import { formatAppDateTime } from '../../services/timezone';
import {
  CheckCircleFilled,
  CheckOutlined,
  ClockCircleOutlined,
  DollarCircleFilled,
  FileTextFilled,
  InboxOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { humanizeEvent } from './financeUtils';
import './finance-components.css';

// ── Status pill ────────────────────────────────────────────────────────────

const STATUS_META = {
  Draft: { label: 'Draft', tone: 'neutral' },
  Ready: { label: 'Ready', tone: 'gold' },
  Invoiced: { label: 'Invoiced', tone: 'info' },
  Processing: { label: 'Processing', tone: 'info', pulse: true },
  Paid: { label: 'Paid', tone: 'success' },
  Overdue: { label: 'Overdue', tone: 'danger' },
  PaymentNotReceived: { label: 'Not Received', tone: 'warning', full: 'Payment Not Received' },
  // client-payment (receipts) statuses derived from Σ receipts vs invoice
  'Partially Received': { label: 'Partially Received', tone: 'gold' },
  Received: { label: 'Received', tone: 'success' },
  'Ready to Pay': { label: 'Ready to Pay', tone: 'info', pulse: true },
  // candidate-payment (disbursements) status — the payroll-side counterpart of
  // 'Partially Received' above, derived from Σ disbursements vs netDisbursement
  PartiallyPaid: { label: 'Partially Paid', tone: 'gold' },
  // day-level statuses used inside the timesheet analysis table
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
  logged: { label: 'Pending', tone: 'gold' },
};

// styleOverride comes from an admin-configured userFieldConfig.valueStyles
// entry ({ color, bg, label }, from financeUtils.getValueStyle) — when
// present it wins over the built-in STATUS_META tone/label, so admins can
// rename a status and recolor it from the Admin panel without a code change.
export function FinanceStatusPill({ status = 'Ready', size, styleOverride }) {
  const meta = STATUS_META[status] ?? { label: String(status), tone: 'neutral' };
  const label = styleOverride?.label ?? meta.label;
  const inlineStyle = styleOverride
    ? { color: styleOverride.color, background: styleOverride.bg, borderColor: styleOverride.color }
    : undefined;
  const pill = (
    <span
      className={`fin-pill${styleOverride ? '' : ` fin-pill--${meta.tone}`}${size === 'sm' ? ' fin-pill--sm' : ''}`}
      style={inlineStyle}
    >
      <span className={`fin-pill__dot${meta.pulse ? ' fin-pill__dot--pulse' : ''}`} />
      {label}
    </span>
  );
  return meta.full ? <Tooltip title={meta.full}>{pill}</Tooltip> : pill;
}

// ── KPI cards ──────────────────────────────────────────────────────────────

export function FinanceKpiRow({ items = [], loading = false, compact = false }) {
  const rowClass = `finance-kpi-row${compact ? ' finance-kpi-row--compact' : ''}`;
  if (loading) {
    const count = items.length || 4;
    return (
      <div className={rowClass}>
        {Array.from({ length: count }, (_, i) => (
          <div className="finance-kpi fin-kpi--skeleton" key={i}>
            <span className="fin-skeleton fin-skeleton--label" />
            <span className="fin-skeleton fin-skeleton--value" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={rowClass}>
      {items.map((item) => (
        <div className={`finance-kpi fin-kpi--${item.tone ?? 'neutral'}`} key={item.key ?? item.label}>
          {item.icon ? <span className="fin-kpi__icon">{item.icon}</span> : null}
          <span className="fin-kpi__body">
            <span className="finance-kpi__label">{item.label}</span>
            <strong className="finance-kpi__value">{item.value}</strong>
            {item.sub ?? item.hint ? <span className="finance-kpi__hint">{item.sub ?? item.hint}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

// Legacy flat day grid — kept for API compatibility.
export function FinanceHoursGrid({ days = [] }) {
  return (
    <div className="finance-hours-grid">
      {days.map((day) => (
        <div className="finance-hours-grid__day" key={day.date}>
          <span>{day.date}</span>
          <strong>{Number(day?.workingHours?.ST ?? 0) + Number(day?.workingHours?.OT ?? 0)}</strong>
        </div>
      ))}
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────

export function FinanceEmptyState({ icon, title, description, action, compact = false }) {
  return (
    <div className={`fin-empty${compact ? ' fin-empty--compact' : ''}`}>
      <span className="fin-empty__icon">{icon ?? <InboxOutlined />}</span>
      <strong className="fin-empty__title">{title}</strong>
      {description ? <span className="fin-empty__desc">{description}</span> : null}
      {action ? <span className="fin-empty__action">{action}</span> : null}
    </div>
  );
}

// ── Section card ───────────────────────────────────────────────────────────

export function FinanceSection({ title, extra, children, empty, className = '', span }) {
  return (
    <section className={`fin-section${span ? ' fin-section--span' : ''}${className ? ` ${className}` : ''}`}>
      {title ? (
        <header className="fin-section__head">
          <h4 className="fin-section__title">{title}</h4>
          {extra ? <span className="fin-section__extra">{extra}</span> : null}
        </header>
      ) : null}
      <div className="fin-section__body">
        {empty ? <FinanceEmptyState compact {...empty} /> : children}
      </div>
    </section>
  );
}

export function FinanceLine({ label, value, hint, strong, accent, negative }) {
  const cls = [
    'fin-line',
    strong ? 'fin-line--strong' : '',
    accent ? 'fin-line--accent' : '',
    negative ? 'fin-line--negative' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <span className="fin-line__label">
        {label}
        {hint != null && hint !== '' ? <span className="fin-line__hint">{hint}</span> : null}
      </span>
      <span className="fin-line__value">{value}</span>
    </div>
  );
}

export function FinanceDivider() {
  return <div className="fin-divider" />;
}

// ── Equation strip ─────────────────────────────────────────────────────────
// terms: [{ value, label, tone: 'default'|'accent'|'strong' } | { op: '×'|'−'|'=' }]

export function FinanceEquation({ terms = [] }) {
  return (
    <div className="fin-equation">
      {terms.map((term, i) =>
        term.op ? (
          <span className="fin-eq__op" key={`op-${i}`}>{term.op}</span>
        ) : (
          <span className={`fin-eq__term${term.tone ? ` fin-eq__term--${term.tone}` : ''}`} key={term.label ?? i}>
            <span className="fin-eq__num">{term.value}</span>
            <span className="fin-eq__lbl">{term.label}</span>
          </span>
        ),
      )}
    </div>
  );
}

// ── Workflow progress steps ────────────────────────────────────────────────
// steps: [{ key, label, state: 'done'|'current'|'pending' }]

export function FinanceWorkflowSteps({ steps = [] }) {
  return (
    <div className="fin-steps">
      {steps.map((step, i) => (
        <div className={`fin-step fin-step--${step.state}`} key={step.key ?? step.label}>
          {i > 0 ? <span className="fin-step__line" /> : null}
          <span className="fin-step__dot">
            {step.state === 'done' ? <CheckOutlined /> : null}
          </span>
          <span className="fin-step__label">{step.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── History timeline ───────────────────────────────────────────────────────
// events: raw workflow entries [{ event, at, by }] — newest rendered first.

const EVENT_ICONS = {
  check: <CheckCircleFilled />,
  dollar: <DollarCircleFilled />,
  sync: <SyncOutlined />,
  file: <FileTextFilled />,
};

function relativeTime(at) {
  if (!at) return '';
  const diffMs = Date.now() - new Date(at).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return '';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

export function FinanceHistoryTimeline({ events = [], emptyState }) {
  const list = (Array.isArray(events) ? [...events] : []).reverse();
  if (!list.length) {
    return (
      <FinanceEmptyState
        icon={<ClockCircleOutlined />}
        title="No activity yet"
        description="Workflow events will appear here as this record moves through its lifecycle."
        {...(emptyState ?? {})}
      />
    );
  }
  return (
    <div className="fin-tl">
      {list.map((entry, i) => {
        const meta = humanizeEvent(entry?.event);
        return (
          <div className="fin-tl__item" key={`${entry?.event}-${entry?.at}-${i}`}>
            <div className="fin-tl__rail">
              <span className={`fin-tl__icon fin-tl__icon--${meta.tone}`}>{EVENT_ICONS[meta.icon] ?? EVENT_ICONS.file}</span>
              {i < list.length - 1 ? <span className="fin-tl__line" /> : null}
            </div>
            <div className="fin-tl__body">
              <div className="fin-tl__head">
                <strong className="fin-tl__label">{meta.label}</strong>
                {entry?.by ? <span className="fin-tl__actor">{entry.by}</span> : null}
              </div>
              <div className="fin-tl__meta">
                {entry?.at ? <span>{formatAppDateTime(entry.at)}</span> : null}
                {entry?.at ? <span className="fin-tl__rel">{relativeTime(entry.at)}</span> : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
